import { ApiProduct, ApiProductVariants } from '../../types';

export interface VariantOption {
	value: string;
	/** the value without its colour code, which is what the customer reads */
	label: string;
	/** the colour a value carries as "Calm 320 <#7fb3c1>", to show instead of the thumbnail */
	swatch: string | null;
	/** the product to go to: the one with this value that keeps most of the shown product's others */
	product: ApiProduct | null;
	selected: boolean;
	/** the shown product's other values that choosing this one changes, empty for an exact match */
	changes: VariantChange[];
}

export interface VariantChange {
	dimension: string;
	label: string;
}

export interface VariantDimensionOptions {
	dimension: string;
	options: VariantOption[];
}

/*
 * A colour value may end in its hex code, "Calm 320 <#7fb3c1>", because a vendor's colours are often
 * not visible on the product photo: a glass board is a glass board whichever colour it is. The code
 * is for the selector to paint a swatch with, never for the customer to read, so it is taken off the
 * label. Anything that is not a plain #rgb or #rrggbb is left in the label as the text it is.
 */
const SWATCH = /\s*<(#(?:[0-9a-f]{3}|[0-9a-f]{6}))>\s*$/i;

export function parseVariantValue(value: string): { label: string; swatch: string | null } {
	const found = value.match(SWATCH);
	return found
		? { label: value.slice(0, found.index).trim(), swatch: found[1].toLowerCase() }
		: { label: value, swatch: null };
}

const sizeOrder = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL', '6XL'];

/**
 * sizes in garment order, then everything numeric-aware so "8" sorts before "10" and "120 cm" before
 * "140 cm". The locale is the site's, never the runtime's: the server renders under one locale and
 * the browser hydrates under another, and the options must come out in the same order in both.
 */
export function compareVariantValues(dimension: string, a: string, b: string, locale: string): number {
	if (dimension === 'SIZE') {
		const ia = sizeOrder.indexOf(a.toUpperCase());
		const ib = sizeOrder.indexOf(b.toUpperCase());
		if (ia !== -1 || ib !== -1) {
			if (ia === -1) return 1;
			if (ib === -1) return -1;
			return ia - ib;
		}
	}
	return parseVariantValue(a).label.localeCompare(parseVariantValue(b).label, locale, {
		numeric: true,
		sensitivity: 'base',
	});
}

/** how many leading characters two values share, which tells "SILK-GLASS®, glas mitt" apart from "Glas mitt" */
function sharedPrefix(a: string, b: string): number {
	let n = 0;
	while (n < a.length && n < b.length && a[n].toLowerCase() === b[n].toLowerCase()) n++;
	return n;
}

/**
 * One list of options per dimension. Each option is the value plus the group member it leads to:
 * the one with that value and the shown product's values on every other dimension, and where no
 * member has that combination, the one keeping as many of them as possible - so in a sparse group
 * every value can still be chosen, and `changes` says what else moves. A board whose middle glass
 * only exists at 2500 mm takes you to 2500 mm in the same colour and glass type, rather than being
 * a dead "not available". Ties go to the member whose differing values read most alike, which
 * keeps Silk-Glass on Silk-Glass.
 */
export function variantOptions(
	variants: ApiProductVariants,
	groupProducts: ApiProduct[],
	currentId: number,
	locale: string
): VariantDimensionOptions[] {
	const current = variants.values[String(currentId)];
	if (!current) return [];
	const productsById = new Map<string, ApiProduct>();
	groupProducts.forEach((p) => productsById.set(String(p.id), p));

	return variants.dimensions.map((dimension) => {
		const values = new Set<string>();
		Object.values(variants.values).forEach((v) => {
			if (v[dimension] !== undefined) values.add(v[dimension]);
		});
		const others = variants.dimensions.filter((d) => d !== dimension);
		const options = [...values]
			.sort((a, b) => compareVariantValues(dimension, a, b, locale))
			.map((value) => {
				let best: { id: string; values: Record<string, string>; kept: number; alike: number } | null = null;
				for (const [id, v] of Object.entries(variants.values)) {
					if (v[dimension] !== value || !productsById.has(id)) continue;
					const kept = others.filter((d) => v[d] === current[d]).length;
					// only the values that would change are compared: one kept is already counted in kept
					const alike = others
						.filter((d) => v[d] !== current[d])
						.reduce((sum, d) => sum + sharedPrefix(v[d] ?? '', current[d] ?? ''), 0);
					if (!best || kept > best.kept || (kept === best.kept && alike > best.alike)) {
						best = { id, values: v, kept, alike };
					}
				}
				return {
					value,
					...parseVariantValue(value),
					product: best ? productsById.get(best.id) ?? null : null,
					selected: value === current[dimension],
					changes: best
						? others
								.filter((d) => best!.values[d] !== current[d] && best!.values[d] !== undefined)
								.map((d) => ({ dimension: d, label: parseVariantValue(best!.values[d]).label }))
						: [],
				};
			});
		return { dimension, options };
	});
}

/**
 * What the product alternatives listing under the page should hold: the group members the selectors
 * are no way to. Without variant data that is the whole group, as it always was; with it, only the
 * members the dropdowns cannot reach - the ones in a sparse group no chain of single-dimension
 * steps leads to, and any member carrying no variant values at all, which no dropdown can offer.
 * Everything the selectors do reach would only be the same products listed twice.
 */
export function variantListingProducts(
	variants: ApiProductVariants | false | undefined,
	groupProducts: ApiProduct[],
	currentId: number,
	locale: string
): ApiProduct[] {
	if (!variants) return groupProducts;
	const unreachable = new Set(unreachableVariants(variants, groupProducts, currentId, locale).map((p) => p.id));
	return groupProducts.filter(
		(p) => p.id !== currentId && (unreachable.has(p.id) || variants.values[String(p.id)] === undefined)
	);
}

/**
 * Group members with variant data the selectors cannot reach from the shown product, in any number
 * of steps: each dropdown changes one dimension at a time, so in a sparse group (black/M and white/L
 * only) neither product leads to the other. The page keeps its plain listing for those.
 */
export function unreachableVariants(
	variants: ApiProductVariants,
	groupProducts: ApiProduct[],
	currentId: number,
	locale: string
): ApiProduct[] {
	const reachable = new Set<number>();
	const queue = [currentId];
	while (queue.length > 0) {
		const id = queue.shift() as number;
		if (reachable.has(id)) continue;
		reachable.add(id);
		variantOptions(variants, groupProducts, id, locale).forEach(({ options }) =>
			options.forEach((o) => {
				// the dropdown does not navigate on the value it already shows, so an option matched to a
				// second product with the same values as this one is no way to that product's page
				if (o.selected) return;
				if (o.product && !reachable.has(o.product.id)) queue.push(o.product.id);
			})
		);
	}
	return groupProducts.filter((p) => variants.values[String(p.id)] !== undefined && !reachable.has(p.id));
}
